import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { getPortalEventActionLabel, getPortalRoleConfig, getSiteRoleLabel } from '../data/siteUsers';
import { useEventManager } from '../hooks/useEventManager';
import { useSiteAuth } from '../hooks/useSiteAuth';

export default function PortalDashboard() {
  const { session } = useSiteAuth();
  const { featuredEvent } = useEventManager();

  if (!session) {
    return null;
  }

  const roleConfig = getPortalRoleConfig(session.role);

  return (
    <motion.div
      className="portal-page"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <section className="portal-hero">
        <div className="portal-hero-copy">
          <p className="portal-kicker">{roleConfig.label} portal</p>
          <h1>{roleConfig.headline}</h1>
          <p>{roleConfig.summary}</p>
          <div className="portal-actions">
            <Link to={roleConfig.primaryActionHref} className="button-primary tap-target">
              {roleConfig.primaryActionLabel}
            </Link>
            <Link to={roleConfig.secondaryActionHref} className="button-secondary tap-target">
              {roleConfig.secondaryActionLabel}
            </Link>
          </div>
        </div>

        <div className="portal-hero-note">
          <span className="portal-role-badge">{getSiteRoleLabel(session.role)}</span>
          <strong>{session.name}</strong>
          <p>{session.email}</p>
          <span>{roleConfig.supportNote}</span>
        </div>
      </section>

      <div className="portal-meta-grid">
        <section className="portal-section">
          <p className="info-kicker">Current focus</p>
          <h2>What this role should help with right now.</h2>
          <ul className="portal-list">
            {roleConfig.focusAreas.map((item) => (
              <li key={item} className="portal-list-item">
                <span />
                <p>{item}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="portal-section">
          <p className="info-kicker">Access snapshot</p>
          <h2>Portal permissions are shaped around the project role.</h2>
          <ul className="portal-list">
            {roleConfig.permissions.map((item) => (
              <li key={item} className="portal-list-item">
                <span />
                <p>{item}</p>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="portal-section portal-section--feature">
        <div className="portal-section-heading">
          <div>
            <p className="info-kicker">Featured event</p>
            <h2>{featuredEvent ? featuredEvent.title : 'No featured event is published yet.'}</h2>
          </div>
          {featuredEvent ? (
            <Link to={`/events/${featuredEvent.slug}`} className="button-ghost tap-target">
              View public event page
            </Link>
          ) : (
            <Link to="/portal/events" className="button-ghost tap-target">
              Open event workspace
            </Link>
          )}
        </div>

        {featuredEvent ? (
          <>
            <p className="portal-feature-copy">{featuredEvent.fullDescription}</p>

            <div className="portal-ticket-list">
              {featuredEvent.tickets.map((ticket) => (
                <article key={ticket.id} className="portal-ticket-row">
                  <div>
                    <p className="info-kicker">{ticket.category.replace('-', ' ')}</p>
                    <h3>{ticket.name}</h3>
                  </div>
                  <strong>{ticket.priceLabel}</strong>
                  <p>{ticket.description}</p>
                </article>
              ))}
            </div>

            <div className="portal-inline-actions">
              <Link to="/portal/events" className="button-secondary tap-target">
                {getPortalEventActionLabel(session.role, featuredEvent)}
              </Link>
              <Link to="/contact" className="button-ghost tap-target">
                Contact the team
              </Link>
            </div>
          </>
        ) : (
          <div className="portal-inline-actions">
            <Link to="/portal/events" className="button-primary tap-target">
              Create or publish an event
            </Link>
          </div>
        )}
      </section>
    </motion.div>
  );
}
